// src/components/applications/MultiApplicationPage.js
import React, { useState, useContext, useEffect, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { AuthContext } from "../../context/AuthContext";
import { SettingsContext } from "../../context/SettingsContext";
import { NotificationContext } from "../../context/NotificationContext";

import { useSelectApplicationMutation } from "../../services/applications";
import { useGetStudentAwardsStatusQuery } from "../../services/students";

import ApplicationHeader from "./ApplicationHeader";
import ApplicationSelection from "./ApplicationSelection";
import AwardedStudentView from "./AwardedStudentView";
import { canStudentsViewAwards } from "./awardVisibility";

import GeneralApplicationBlankPage from "../gen-application/GeneralApplicationBlankPage";
import DasApplicationBlankPage from "../das-application/DasApplicationBlankPage";

function MultiApplicationPage({
  overrideUser,
  isAdminView = false,
  onOverrideUserChange,
}) {
  const { user, updateUser, isGenAppOverrideActive, isDasAppOverrideActive } =
    useContext(AuthContext);
  const effectiveUser = overrideUser || user;
  const { settings, isGenAppOpen, isDASAppOpen } = useContext(SettingsContext);
  const { addNotification } = useContext(NotificationContext);
  const canUserEditDas =
    isDasAppOverrideActive ||
    (isDASAppOpen && effectiveUser?.das_application_status !== "Submitted");
  const canUserEditGen =
    isGenAppOverrideActive ||
    (isGenAppOpen && effectiveUser?.application_status !== "Submitted");

  const [selectApplication] = useSelectApplicationMutation();
  const location = useLocation();
  const navigate = useNavigate();
  // console.log("effectiveUser", effectiveUser);
  // console.log("settings", settings);
  const canViewAwards = isAdminView || canStudentsViewAwards(settings);
  const { data: awardsData } = useGetStudentAwardsStatusQuery(undefined, {
    skip: !canViewAwards,
  });
  const hasAwards =
    canViewAwards &&
    awardsData &&
    awardsData?.data &&
    awardsData?.data?.length > 0;

  const [selectedApplications, setSelectedApplications] = useState([]);
  const [agreementLocal, setAgreementLocal] = useState(null);

  const [activeAppTab, setActiveAppTab] = useState(0);

  const [isSaveChanges, setIsSaveChanges] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [isGeneralDirty, setIsGeneralDirty] = useState(false);
  const [isDasDirty, setIsDasDirty] = useState(false);

  const generalClose = settings["GeneralClose"]
    ? settings["GeneralClose"].settingDate
    : null;
  const dasClose = settings["DASClose"]
    ? settings["DASClose"].settingDate
    : null;
  const noticeMessage = settings["Notice"] ? settings["Notice"].value : null;
  const scholarshipYear = settings["Scholarship Year"]
    ? settings["Scholarship Year"].value
    : null;

  const [generalAppStatus, setGeneralAppStatus] = useState("success"); // 'error', 'warning', 'success'
  const [dasAppStatus, setDasAppStatus] = useState("success"); // 'error', 'warning', 'success'

  useEffect(() => {
    const apps = [];
    if (effectiveUser?.generalApp) {
      apps.push("general");
    }
    if (effectiveUser?.dasApp) {
      apps.push("das");
    }

    setSelectedApplications(apps);
    if (overrideUser) {
      setAgreementLocal("yes");
    } else {
      setAgreementLocal(apps.length > 0 ? "yes" : null);
    }
  }, [effectiveUser, overrideUser]);

  useEffect(() => {
    if (selectedApplications.length === 0) return;

    const totalTabs =
      1 +
      (selectedApplications.includes("general") ? 1 : 0) +
      (selectedApplications.includes("das") ? 1 : 0);

    const queryParams = new URLSearchParams(location.search);
    const requestedTab = parseInt(queryParams.get("appTab") || "0", 10);

    let clampedTab = Math.min(requestedTab, totalTabs - 1);
    if (clampedTab < 0) clampedTab = 0;

    if (activeAppTab !== clampedTab) {
      setActiveAppTab(clampedTab);
    }
    // eslint-disable-next-line
  }, [selectedApplications]);

  useEffect(() => {
    if (selectedApplications.length === 0) return;

    const totalTabs =
      1 +
      (selectedApplications.includes("general") ? 1 : 0) +
      (selectedApplications.includes("das") ? 1 : 0);

    if (activeAppTab >= totalTabs) {
      setActiveAppTab(totalTabs - 1);
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const currentParam = parseInt(queryParams.get("appTab") || "0", 10);
    if (currentParam !== activeAppTab) {
      queryParams.set("appTab", String(activeAppTab));
      navigate(`${location.pathname}?${queryParams.toString()}`, {
        replace: true,
      });
    }
  }, [
    activeAppTab,
    selectedApplications,
    location.search,
    location.pathname,
    navigate,
  ]);

  const handleTabChange = (event, newValue) => {
    const currentAppType =
      activeAppTab === 0
        ? null
        : selectedApplications.includes("general") && activeAppTab === 1
        ? "general"
        : selectedApplications.includes("das") &&
          (selectedApplications.includes("general")
            ? activeAppTab === 2
            : activeAppTab === 1)
        ? "das"
        : null;

    const isDirty = currentAppType === "general" ? isGeneralDirty : isDasDirty;

    if (activeAppTab > 0) {
      if (currentAppType === "general") {
        if (!canUserEditGen) {
          setActiveAppTab(newValue);
        } else if (isDirty) {
          setIsSaveChanges(true);
          setPendingTab(newValue);
        } else {
          setActiveAppTab(newValue);
        }
      } else if (currentAppType === "das") {
        if (!canUserEditDas) {
          setActiveAppTab(newValue);
        } else if (isDirty) {
          setIsSaveChanges(true);
          setPendingTab(newValue);
        } else {
          setActiveAppTab(newValue);
        }
      }
    } else {
      if (selectedApplications.length > 0) {
        setActiveAppTab(newValue);
      } else {
        if (newValue === 0) {
          setActiveAppTab(newValue);
        } else {
          addNotification(
            "Please select at least one application type before proceeding.",
            "error"
          );
        }
      }
    }
  };

  const handleSubmit = async () => {
    if (agreementLocal !== "yes") {
      addNotification("You must agree to proceed.", "error");
      return;
    }

    if (selectedApplications.length === 0) {
      addNotification("Please select at least one application type!", "error");
      return;
    }

    try {
      const result = await selectApplication({
        userID: user?.id,
        applicationType: selectedApplications,
      }).unwrap();

      if (result?.success) {
        updateUser({
          generalApp: selectedApplications.includes("general") ? true : false,
          dasApp: selectedApplications.includes("das") ? true : false,
          info_screen_acknowledged: result?.data || null,
        });

        setTimeout(() => {
          setActiveAppTab(1);
          setIsSaveChanges(false);
        }, 750);
      }
    } catch (error) {
      console.error("Error selecting application:", error);
      addNotification(
        error?.data?.message ||
          "There was an error processing your application selection. Please try again.",
        "error"
      );
    }
  };

  const handleAgreeChange = (value) => {
    if (value === "yes" || value === "no") {
      setAgreementLocal(value);
      if (value !== "yes") {
        setSelectedApplications([]);
      }
    }
  };

  const handleGeneralAppStatusChange = (status) => {
    setGeneralAppStatus(status);
  };

  const handleDasAppStatusChange = (status) => {
    setDasAppStatus(status);
  };

  const showSelectTab = !overrideUser;
  let genTabIndex = showSelectTab ? 1 : 0;
  let dasTabIndex = showSelectTab
    ? selectedApplications?.includes("general")
      ? 2
      : 1
    : selectedApplications?.includes("general")
    ? 1
    : 0;

  return (
    <div className="multi-application-page">
      <ApplicationHeader
        user={effectiveUser}
        isAdminView={isAdminView}
        onUserUpdate={overrideUser ? onOverrideUserChange : updateUser}
        generalClose={generalClose}
        dasClose={dasClose}
        noticeMessage={noticeMessage}
      />
      {selectedApplications.length > 0 && (
        <Tabs
          value={activeAppTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="inherit"
          aria-label="Application Type Tabs"
          sx={{
            width: "90%",
            marginBottom: 0,
            paddingBottom: 0,
            display: "flex",
            justifyContent: "center",
            "& .MuiTabs-scroller": {
              flexGrow: 0,
            },
            "& .MuiTab-root": {
              backgroundColor: "#eee",
              color: "#000000",
              textTransform: "none",
              borderRadius: "15px 15px 0 0",
              border: "1px solid #a3a3a3",
              marginBottom: 0,
              paddingBottom: 0,
              "&.Mui-selected": {
                backgroundColor: "#3e9d5f",
                color: "#ffffff",
              },
              "&:hover": {
                backgroundColor: "#5DC1AF",
              },
            },
          }}
        >
          {showSelectTab && !hasAwards && <Tab label="Select Applications" />}
          {hasAwards && <Tab label="Awarded" />}
          {agreementLocal === "yes" &&
            effectiveUser?.info_screen_acknowledged &&
            selectedApplications?.includes("general") && (
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span>General Application</span>
                    {generalAppStatus === "error" && (
                      <ErrorOutlineIcon
                        color="error"
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {generalAppStatus === "warning" && (
                      <WarningAmberIcon
                        color="warning"
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {generalAppStatus === "success" && (
                      <CheckCircleIcon
                        color="success"
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                }
              />
            )}

          {agreementLocal === "yes" &&
            effectiveUser?.info_screen_acknowledged &&
            selectedApplications?.includes("das") && (
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span>District/Authority Application</span>
                    {dasAppStatus === "error" && (
                      <ErrorOutlineIcon
                        color="error"
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {dasAppStatus === "warning" && (
                      <WarningAmberIcon
                        color="warning"
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {dasAppStatus === "success" && (
                      <CheckCircleIcon
                        color="success"
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                }
              />
            )}
        </Tabs>
      )}
      {!hasAwards && showSelectTab && activeAppTab === 0 && (
        <ApplicationSelection
          agreementLocal={agreementLocal}
          selectedApplications={selectedApplications}
          setSelectedApplications={setSelectedApplications}
          generalClose={generalClose}
          dasClose={dasClose}
          handleSelectApplicationSubmit={handleSubmit}
          handleAgreeChange={handleAgreeChange}
        />
      )}
      {hasAwards && activeAppTab === 0 && (
        <AwardedStudentView isAdminView={isAdminView} />
      )}
      {selectedApplications?.includes("general") &&
        activeAppTab === genTabIndex && (
          <GeneralApplicationBlankPage
            scholarshipYear={scholarshipYear}
            isSaveChanges={isSaveChanges}
            onSaveComplete={() => {
              if (pendingTab !== null) {
                setActiveAppTab(pendingTab);
                setPendingTab(null);
              }
              setIsSaveChanges(false);
            }}
            isDirty={isGeneralDirty}
            setIsDirty={setIsGeneralDirty}
            onStatusChange={handleGeneralAppStatusChange}
            overrideUser={overrideUser}
          />
        )}
      {selectedApplications?.includes("das") &&
        activeAppTab === dasTabIndex && (
          <DasApplicationBlankPage
            scholarshipYear={scholarshipYear}
            isSaveChanges={isSaveChanges}
            onSaveComplete={() => {
              if (pendingTab !== null) {
                setActiveAppTab(pendingTab);
                setPendingTab(null);
              }
              setIsSaveChanges(false);
            }}
            isDirty={isDasDirty}
            setIsDirty={setIsDasDirty}
            onStatusChange={handleDasAppStatusChange}
            overrideUser={overrideUser}
          />
        )}
    </div>
  );
}

export default memo(MultiApplicationPage);
